import { Check, X as XIcon } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Eyebrow, SectionHeading, BookDemoButton } from "@/components/marketing/Primitives";
import { pricingTiers, faqs } from "@/lib/content";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const compareRows = [
  { label: "Customer & Job Management", s: true, p: true, e: true },
  { label: "Operations Calendar", s: true, p: true, e: true },
  { label: "Engineer Mobile App", s: true, p: true, e: true },
  { label: "Digital Jobsheets", s: true, p: true, e: true },
  { label: "Stock Control", s: true, p: true, e: true },
  { label: "Asset Management", s: false, p: true, e: true },
  { label: "Service Plans & Contracts", s: false, p: true, e: true },
  { label: "Customer Portal", s: false, p: true, e: true },
  { label: "Sales Pipeline", s: false, p: true, e: true },
  { label: "AI Insights", s: false, p: true, e: true },
  { label: "Dedicated Onboarding Manager", s: false, p: false, e: true },
  { label: "Data Migration Service", s: false, p: false, e: true },
  { label: "On-site Guidance & SLAs", s: false, p: false, e: true },
];

function Cell({ ok }) {
  return ok ? (
    <Check size={18} weight="bold" className="mx-auto text-primary" />
  ) : (
    <XIcon size={16} weight="bold" className="mx-auto text-white/25" />
  );
}

export default function Pricing() {
  return (
    <>
      <SEO
        title="Pricing — FlowCore Field Service Platform"
        description="Simple FlowCore pricing: Standard £99+VAT/user/month, Premium £199+VAT/user/month, and Elite with dedicated site guidance (price on request). Book a demo today."
      />
      <section className="relative overflow-hidden pt-36 pb-12">
        <div className="pointer-events-none absolute inset-0 radial-fade" />
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <Eyebrow className="mx-auto">Pricing</Eyebrow>
            <h1 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Simple, scalable pricing
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-muted-foreground md:text-lg">
              Per-user pricing that grows with your team. Contact us for onboarding and migration services.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Stagger className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {pricingTiers.map((t) => (
              <StaggerItem key={t.name}>
                <div className={`relative flex h-full flex-col rounded-3xl border p-8 ${t.popular ? "border-primary/50 bg-card glow-green" : "border-white/10 bg-card/60"}`}>
                  {t.popular && (
                    <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">Most Popular</span>
                  )}
                  <h3 className="font-heading text-xl font-semibold text-white">{t.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{t.tagline}</p>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-extrabold text-white">{t.price}</span>
                    <span className="text-sm text-muted-foreground">{t.period}</span>
                  </div>
                  <ul className="mt-6 flex-1 space-y-3">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <Check size={16} weight="bold" className="mt-0.5 flex-none text-primary" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    <BookDemoButton
                      label={t.cta}
                      type={t.name === "Elite" ? "contact" : "demo"}
                      testid={`pricing-${t.name.toLowerCase()}`}
                      className={`w-full justify-center ${t.popular ? "" : "bg-white/10 text-white hover:bg-white/15 hover:shadow-none"}`}
                    />
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            All prices exclude VAT. Contact us for onboarding and migration services.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="Compare plans" title="What's included in each plan" />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-x-auto rounded-3xl border border-white/10">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="bg-card">
                    <th className="p-4 text-left font-semibold text-muted-foreground">Feature</th>
                    <th className="p-4 text-center font-semibold text-white">Standard</th>
                    <th className="p-4 text-center font-semibold text-primary">Premium</th>
                    <th className="p-4 text-center font-semibold text-white">Elite</th>
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((r, i) => (
                    <tr key={r.label} className={i % 2 ? "bg-card/40" : "bg-transparent"}>
                      <td className="p-4 text-left text-white">{r.label}</td>
                      <td className="p-4 text-center"><Cell ok={r.s} /></td>
                      <td className="p-4 text-center"><Cell ok={r.p} /></td>
                      <td className="p-4 text-center"><Cell ok={r.e} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="FAQ" title="Frequently asked questions" />
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8 space-y-3" data-testid="pricing-faq">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-white/10 bg-card px-5">
                  <AccordionTrigger className="text-left font-heading text-base font-semibold text-white hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 text-center">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">Not sure which plan fits?</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Book a demo and we'll recommend the right plan for your team size and goals.</p>
          <div className="mt-8 flex justify-center">
            <BookDemoButton testid="pricing-cta-demo" />
          </div>
        </div>
      </section>
    </>
  );
}
